import { uid } from './constants.js';
import { SCENE_PROPS } from './sceneProps.js';

export const OBJECT_ROW_LIMIT = 100;
const kinds = new Set(['column', 'shelf', 'counter', 'box', 'desk', 'chair', ...Object.keys(SCENE_PROPS)]);
const finite = (value, min, max) => Number.isFinite(value) && value >= min && value <= max;
const validID = value => typeof value === 'string' && value.trim() === value && value.length > 0 && [...value].length <= 80;
const point = (x, y) => finite(x, -100000, 100000) && finite(y, -100000, 100000);
const norm = angle => ((angle % 360) + 360) % 360;
const trig = angle => {
  const radians = norm(angle) * Math.PI / 180;
  const exact = value => Math.abs(value) < 1e-15 ? 0 : value;
  return { cos: exact(Math.cos(radians)), sin: exact(Math.sin(radians)) };
};

function selectedObject(plan, objectID) {
  if (!plan || plan._siteLevels || plan._groundSelections || plan._sceneWalls) throw new Error('Choose an object on its own editable floor before repeating it.');
  if (!validID(plan.activeFloorId)) throw new Error('Choose a floor with a valid identity before repeating objects.');
  if (!Array.isArray(plan.obstacles) || plan.obstacles.length > 500) throw new Error('This floor has an invalid or oversized object list.');
  const ids = new Set();
  for (const object of plan.obstacles) {
    if (!object || !validID(object.id) || ids.has(object.id)) throw new Error('Objects need valid unique IDs before a row can be created.');
    ids.add(object.id);
  }
  const object = validID(objectID) && plan.obstacles.find(item => item.id === objectID);
  if (!object) throw new Error('The selected object no longer exists on this floor.');
  if (!kinds.has(object.kind) || object.objectKind || object.wallPts || object.roofPts) throw new Error('Choose ordinary furniture, shelving or a column. Vehicles, poles and special geometry cannot be repeated as a row.');
  if (['wallId', 'poleId', 'mountPoleId', 'parentId', 'attachedTo'].some(key => object[key])
    || plan.cams?.some(camera => camera.mountPoleId === objectID)
    || plan.lights?.some(light => light.poleId === objectID)) throw new Error('This object has attachments. Review those objects individually instead of repeating them.');
  if (!point(object.x, object.y) || !['width', 'depth'].every(key => finite(object[key], .05, 1000))
    || !finite(object.height, .05, 100) || !finite(object.bottom ?? 0, 0, 100000)
    || !finite(object.pan ?? 0, -360000, 360000)) throw new Error('Review the object geometry first: dimensions must be valid, height at most 100 m, and position within supported Site coordinates.');
  return object;
}

/** A lightweight eligibility result for selection controls; geometry review still
 * validates every requested placement and never mutates the supplied document. */
export function repeatableRowObject(plan, objectID) {
  try { selectedObject(plan, objectID); return { supported: true, reason: '' }; }
  catch (error) { return { supported: false, reason: error.message }; }
}

function withinBounds(object) {
  const { cos, sin } = trig(object.pan ?? 0);
  const halfX = (Math.abs(cos) * object.width + Math.abs(sin) * object.depth) / 2;
  const halfY = (Math.abs(sin) * object.width + Math.abs(cos) * object.depth) / 2;
  return point(object.x - halfX, object.y - halfY) && point(object.x + halfX, object.y + halfY)
    && (object.bottom ?? 0) + object.height <= 100000;
}

/** Repeated rows are explicit assumptions, not survey observations. Direction
 * follows plan XY (0° right, 90° down); object heading remains unchanged.
 * Memoize this result while reviewing, then apply its doc once so IDs stay stable. */
export function reviewObjectRow(plan, objectID, { count, spacingMetres, direction } = {}) {
  const original = selectedObject(plan, objectID);
  if (!Number.isInteger(count) || count < 2 || count > OBJECT_ROW_LIMIT) throw new Error('Enter 2–100 objects, including the original.');
  if (plan.obstacles.length + count - 1 > 500) throw new Error('This row would exceed the floor limit of 500 objects.');
  if (!finite(spacingMetres, .05, 100000)) throw new Error('Enter centre-to-centre spacing from 0.05 to 100000 m.');
  if (!finite(direction, -360000, 360000)) throw new Error('Enter a finite direction within ±360000°.');
  if (!withinBounds(original)) throw new Error('The original object extends beyond supported Site coordinates.');
  const heading = norm(direction), { cos, sin } = trig(heading);
  const dx = spacingMetres * cos, dy = spacingMetres * sin;
  const orientation = trig(original.pan ?? 0);
  // Identical oriented rectangles overlap iff both local-axis projections do.
  // Boundary contact is allowed; this is not a collision test for the whole Site.
  const consecutiveOverlap = Math.abs(dx * orientation.cos + dy * orientation.sin) < original.width - 1e-9
    && Math.abs(-dx * orientation.sin + dy * orientation.cos) < original.depth - 1e-9;
  const row = { source: 'assumed-repeated-spacing', sourceObjectID: objectID,
    recordedFloorID: plan.activeFloorId, count, spacingMetres, direction: heading,
    origin: { x: original.x, y: original.y }, consecutiveOverlap };
  const doc = structuredClone(plan), added = [], usedIDs = new Set(plan.obstacles.map(object => object.id));
  const template = structuredClone(original), templateEvidence = {};
  for (const key of ['capture', 'measurements']) {
    if (Object.hasOwn(template, key)) { templateEvidence[key] = template[key]; delete template[key]; }
  }
  // A repeated row can itself be a new template without growing recursive history.
  delete template.rowPlacement;
  for (let index = 2; index <= count; index++) {
    let id;
    for (let attempt = 0; attempt < 16; attempt++) { const candidate = uid('obj'); if (!usedIDs.has(candidate)) { id = candidate; break; } }
    if (!id) throw new Error('Could not allocate a unique object identity. Try reviewing the row again.');
    usedIDs.add(id);
    const copy = { ...structuredClone(template), id, name: `${original.name || 'Object'} · row ${index}`,
      x: original.x + (index - 1) * dx, y: original.y + (index - 1) * dy,
      rowPlacement: { ...structuredClone(row), index,
        ...(Object.keys(templateEvidence).length ? { templateEvidence: structuredClone(templateEvidence) } : {}) } };
    if (!withinBounds(copy)) throw new Error('This row extends beyond supported Site coordinates. Reduce the spacing or count, or change direction.');
    added.push(copy); doc.obstacles.push(copy);
  }
  doc.review = null;
  return { doc, original: structuredClone(original), added: structuredClone(added), row,
    warnings: [
      'Repeated spacing is an assumption, not a measured position. Verify representative objects and exceptions on site.',
      'Object headings are unchanged. Review walls, other objects, access routes and camera coverage; this preview does not establish clearances.',
      ...(consecutiveOverlap ? ['Consecutive object footprints overlap at this spacing. Review the spacing before applying this row.'] : []),
      ...(Object.keys(templateEvidence).length ? ['Original capture and measurement evidence belongs to the template only; copies retain it as template evidence.'] : [])
    ] };
}
